import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  Input,
  Toggle,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Header,
  FormSection,
  FormActions,
  FormRow,
} from "../create/styles";

const ProjectEdit: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const { id } = useParams<{ id: string }>();
  usePageTitle(t("EDIT_PROJECT"));

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [project, setProject] = useState<any>(null);

  useEffect(() => {
    if (!id) {
      navigate("/openstack/projects");
      return;
    }

    projectService
      .getProjectDetail(id)
      .then((res) => {
        setProject(res.data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_PROJECT");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/projects");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [id, t, viewAlert, navigate]);

  const validationSchema = yup.object().shape({
    name: yup.string().required(t("REQUIRED_FIELD")),
    description: yup.string().required(t("REQUIRED_FIELD")),
    disabled: yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      name: project?.name || "",
      description: project?.description || "",
      disabled: project?.disabled || false,
    },
    onSubmit: (formValues) => {
      if (!id) return;

      setUpdateLoading(true);
      setPageLoading(true);

      projectService
        .updateProject(id, formValues)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("PROJECT_UPDATED_SUCCESSFULLY"),
          });
          navigate("/openstack/projects");
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_UPDATE_PROJECT"),
            });
          }
        })
        .finally(() => {
          setUpdateLoading(false);
          setPageLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setErrors,
  } = formik;

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  if (pageLoading || !project) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("LOADING")}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath="/openstack/projects" />
        <Typography variant="h1">{t("EDIT_PROJECT")}</Typography>
      </Header>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <FormRow>
          <Input
            required
            title={t("PROJECT_NAME")}
            placeholder={t("ENTER_PROJECT_NAME")}
            fieldName="name"
            {...formikProps}
          />
        </FormRow>

        <FormRow>
          <Input
            required
            title={t("DESCRIPTION")}
            placeholder={t("ENTER_DESCRIPTION")}
            fieldName="description"
            isTextArea
            {...formikProps}
          />
        </FormRow>

        <FormRow>
          <Toggle
            value={values.disabled}
            onChange={() => setFieldValue("disabled", !values.disabled)}
            title={t("DISABLED")}
          />
        </FormRow>

        <FormActions>
          <Button
            variant="stroke"
            size="display"
            title={t("CANCEL")}
            onClick={() => navigate("/openstack/projects")}
          />
          <Button
            variant="primary"
            size="display"
            title={t("UPDATE")}
            loading={updateLoading}
            onClick={onFormSubmit}
          />
        </FormActions>
      </FormSection>
    </Container>
  );
};

export default ProjectEdit;
